import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./css/awarenessWrapUp.css";
import useGameState from "../hooks/useGameState";
import useTranslation from "../hooks/useTranslation";

// Recursos
import edificos from "../assets/recursosJuego/EDIFICIOS-01.png";

function AwarenessWrapUp({ updateProgress }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { selectedCharacters } = useGameState();
  const players = selectedCharacters?.playersArray || [];
  
  // Función helper para obtener el nombre traducido del personaje
  const getCharacterName = (characterId) => {
    return t(`characterSelection.characters.${characterId}.name`);
  };
  
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [playerSelections, setPlayerSelections] = useState({}); // { characterId: [risk1, risk2, risk3] }
  const [allRiskSelections, setAllRiskSelections] = useState({}); // { riskIndex: [player1, player2, ...] }

  const currentPlayer = players[currentPlayerIndex];

  // Función para scroll suave y lento personalizado
  const smoothScrollToTop = () => {
    const container = document.querySelector('.wrapup-wrapper');
    const targetElement = document.querySelector('.wrapup-header');
    
    if (!container || !targetElement) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const startPosition = container.scrollTop;
    const distance = -startPosition;
    const duration = 1200; // Duración más larga (1.2 segundos)
    let startTime = null;

    const animation = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      
      // Ease-in-out para suavidad extra
      const ease = progress < 0.5 
        ? 2 * progress * progress 
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      
      container.scrollTop = startPosition + (distance * ease);
      
      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  };

  // Las 10 respuestas más importantes de la fase Awareness
  const topRisks = t('awarenessWrapup.risks');

  const handleRiskSelect = (riskIndex) => {
    const currentSelections = playerSelections[currentPlayer.characterId] || [];

    let newSelections;
    if (currentSelections.includes(riskIndex)) {
      // Deseleccionar
      newSelections = currentSelections.filter(r => r !== riskIndex);
    } else {
      // Seleccionar
      if (currentSelections.length >= 3) {
        // Si ya tiene 3, reemplazar el último por el nuevo
        newSelections = [...currentSelections.slice(0, 2), riskIndex];
      } else {
        // Si tiene menos de 3, simplemente agregar
        newSelections = [...currentSelections, riskIndex];
      }
    }

    setPlayerSelections({
      ...playerSelections,
      [currentPlayer.characterId]: newSelections
    });

    // Actualizar el objeto de todos los riesgos con avatares
    const updatedAllRisks = { ...allRiskSelections };
    
    // Limpiar este jugador de todos los riesgos
    Object.keys(updatedAllRisks).forEach(key => {
      updatedAllRisks[key] = updatedAllRisks[key].filter(
        p => p.characterId !== currentPlayer.characterId
      );
    });

    // Agregar este jugador a los riesgos seleccionados
    newSelections.forEach(risk => {
      if (!updatedAllRisks[risk]) {
        updatedAllRisks[risk] = [];
      }
      updatedAllRisks[risk].push(currentPlayer);
    });

    setAllRiskSelections(updatedAllRisks);
  };

  const handleNextPlayer = () => {
    const currentSelections = playerSelections[currentPlayer.characterId] || [];
    
    if (currentSelections.length !== 3) {
      alert(t('awarenessWrapup.alertSelectThree'));
      return;
    }

    if (currentPlayerIndex < players.length - 1) {
      // Pasar al siguiente jugador
      setCurrentPlayerIndex(currentPlayerIndex + 1);
      // Scroll hacia arriba suave y lento
      setTimeout(() => {
        smoothScrollToTop();
      }, 100);
    } else {
      // Todos terminaron - guardar y continuar
      localStorage.setItem('greenscape_risk_selections', JSON.stringify(playerSelections));
      updateProgress('awareness-wrapup');
      navigate('/awareness-reflection');
    }
  };

  const handlePreviousPlayer = () => {
    if (currentPlayerIndex > 0) {
      setCurrentPlayerIndex(currentPlayerIndex - 1);
      // Scroll hacia arriba suave y lento
      setTimeout(() => {
        smoothScrollToTop();
      }, 100);
    }
  };

  const currentSelections = playerSelections[currentPlayer.characterId] || [];
  const isComplete = currentSelections.length === 3;

  return (
    <div className="wrapup-wrapper">
      {/* Header con título */}
      <div className="wrapup-header">
        <h1 className="wrapup-title">{t('awarenessWrapup.title')}</h1>
        <p className="wrapup-subtitle">{t('awarenessWrapup.subtitle')}</p>
      </div>

      {/* Instrucciones */}
      <div className="wrapup-instructions">
        <h2>{t('awarenessWrapup.congratsTitle')}</h2>
        <p>{t('awarenessWrapup.paragraph1')}</p>
        <p>{t('awarenessWrapup.paragraph2')}</p>
        
        <div className="instruction-box">
          <h3>{t('awarenessWrapup.taskTitle')}</h3>
          <p>{t('awarenessWrapup.taskParagraph1')}</p>
          <p>{t('awarenessWrapup.taskParagraph2')}</p>
          <p>{t('awarenessWrapup.taskParagraph3')}</p>
        </div>

        <div className="question-box">
          {t('awarenessWrapup.questionBoxTitle')}
        </div>
      </div>

      {/* Jugador actual */}
      <div className="current-player-indicator">
        <div 
          className="player-badge"
          style={{ backgroundColor: currentPlayer.color }}
        >
          <img src={currentPlayer.image} alt={getCharacterName(currentPlayer.characterId)} className="player-badge-avatar" />
          <div className="player-badge-info">
            <span className="player-badge-name">{getCharacterName(currentPlayer.characterId)}</span>
            <span className="player-badge-label">{t('awarenessWrapup.yourTurnLabel')}</span>
          </div>
        </div>
        <div className="selection-counter">
          <span className={`counter-number ${isComplete ? 'complete' : ''}`}>
            {currentSelections.length} / 3
          </span>
          <span className="counter-label">{t('awarenessWrapup.risksSelectedLabel')}</span>
        </div>
      </div>

      {/* Grid de riesgos */}
      <div className="risks-grid">
        {topRisks.map((risk, index) => {
          const isSelected = currentSelections.includes(index);
          const playersOnThisRisk = allRiskSelections[index] || [];
          
          return (
            <div
              key={index}
              className={`risk-card ${isSelected ? 'selected' : ''}`}
              onClick={() => handleRiskSelect(index)}
              style={{
                borderColor: isSelected ? currentPlayer.color : '#ddd',
                boxShadow: isSelected ? `0 0 15px ${currentPlayer.color}50` : '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <div className="risk-number">#{index + 1}</div>
              <p className="risk-text">{risk}</p>
              
              {/* Avatares de jugadores que han seleccionado este riesgo */}
              {playersOnThisRisk.length > 0 && (
                <div className="risk-avatars">
                  {playersOnThisRisk.map((player) => (
                    <img
                      key={player.characterId}
                      src={player.image}
                      alt={getCharacterName(player.characterId)}
                      className="risk-avatar"
                      style={{ borderColor: player.color }}
                      title={getCharacterName(player.characterId)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Botones de navegación */}
      <div className="wrapup-actions">
        {currentPlayerIndex > 0 && (
          <button 
            className="btn-previous"
            onClick={handlePreviousPlayer}
          >
            {t('awarenessWrapup.previousButton')}
          </button>
        )}
        
        <button 
          className={`btn-next ${isComplete ? 'active' : 'disabled'}`}
          onClick={handleNextPlayer}
          disabled={!isComplete}
          style={{ 
            backgroundColor: isComplete ? currentPlayer.color : '#9E9E9E' 
          }}
        >
          {currentPlayerIndex === players.length - 1 ? t('awarenessWrapup.continueButton') : t('awarenessWrapup.nextButton')}
        </button>
      </div>

      {/* Skyline decorativo */}
      <img src={edificos} alt="Skyline" className="wrapup-skyline" />
    </div>
  );
}

export default AwarenessWrapUp;

